import { useState, useCallback } from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/auth.service';
import { handleApiError } from '@/utils/errorHandler';

export interface UseLoginResult {
  // Form state
  email: string;
  setEmail: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  showPassword: boolean;
  setShowPassword: (value: boolean) => void;
  
  // Validation errors
  emailError: string;
  passwordError: string;
  
  // Loading state
  loading: boolean;
  
  // Error state
  error: string | null;
  
  // Actions
  handleEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  handleForgotPassword: () => void;
}

/**
 * Hook xử lý logic đăng nhập
 * - Validate email và password
 * - Gọi API login
 * - Điều hướng theo role sau khi login thành công
 */
export const useLogin = (): UseLoginResult => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  /**
   * Validate email format
   * - Kiểm tra email không rỗng
   * - Kiểm tra format email hợp lệ (có @ và domain)
   */
  const validateEmail = useCallback((emailValue: string): boolean => {
    if (!emailValue) {
      setEmailError('Vui lòng nhập email!');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailValue)) {
      setEmailError('Email không hợp lệ!');
      return false;
    }
    setEmailError('');
    return true;
  }, []);

  /**
   * Validate password
   * - Kiểm tra password không rỗng
   */
  const validatePassword = useCallback((passwordValue: string): boolean => {
    if (!passwordValue) {
      setPasswordError('Vui lòng nhập mật khẩu!');
      return false;
    }
    setPasswordError('');
    return true;
  }, []);

  /**
   * Xử lý thay đổi email
   * - Cập nhật email state
   * - Validate lại nếu đã có lỗi trước đó
   */
  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (emailError) {
      validateEmail(newEmail);
    }
  }, [emailError, validateEmail]);

  /**
   * Xử lý thay đổi password
   * - Cập nhật password state
   * - Validate lại nếu đã có lỗi trước đó
   */
  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    if (passwordError) {
      validatePassword(newPassword);
    }
  }, [passwordError, validatePassword]);

  /**
   * Xử lý submit form đăng nhập
   * - Validate email và password
   * - Gọi API login
   * - Điều hướng theo role: staff → /personal/overview, admin → /
   */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);
    
    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      await login(email, password);
      message.success('Đăng nhập thành công!');
      
      // Lấy thông tin user từ storage để điều hướng theo role
      const storedUser = await authService.getStoredUser();
      if (storedUser?.role === 'staff') {
        navigate('/personal/overview', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Đăng nhập thất bại. Vui lòng thử lại.';
      handleApiError(err, errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [email, password, validateEmail, validatePassword, login, navigate]);

  /**
   * Điều hướng đến trang quên mật khẩu
   */
  const handleForgotPassword = useCallback(() => {
    navigate('/forgot-password');
  }, [navigate]);

  return {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    setShowPassword,
    emailError,
    passwordError,
    loading,
    error,
    handleEmailChange,
    handlePasswordChange,
    handleSubmit,
    handleForgotPassword,
  };
};

